import { type ChangeEvent, useState } from 'react';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    useRemovePaymentGateway,
    useUpdatePaymentGateway,
} from '@travelity/api';

import { Button } from '@travelity/ui/button';
import { useForm } from '@travelity/form';
import { TextField } from '@travelity/form/controls';
import { useUserContext } from '@contexts/user';
import { useToast } from '../../../hooks';

import { PaymentProviderCard } from './payment-provider-card';
import { PaymentProviderHeader } from './payment-provider-header';
import { PaymentProviderDisableModal } from './payment-provider-disable-modal';
import type {
    PaymentGatewayFormValues,
    PaymentProviderPageProps,
} from './types';
import { buildDefaultValues, buildPayload } from './heleprs';

const PaymentProviderPage = ({
    provider,
    title,
    fields,
}: PaymentProviderPageProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { t } = useTranslation();
    const { showError, showSuccess } = useToast();
    const { user } = useUserContext();
    const workspace = user?.workspaces?.selected;
    const paymentGateway = user?.paymentGateways?.[0];

    const defaultValues = buildDefaultValues(paymentGateway);
    const { mutate: updatePaymentGateway, isLoading: isUpdating } =
        useUpdatePaymentGateway(workspace);
    const { mutate: removePaymentGateway, isLoading: isRemoving } =
        useRemovePaymentGateway(workspace);

    const isSaving = isUpdating || isRemoving;

    const { Form, watch, setValue, getValues, clearErrors, errorFields } =
        useForm<PaymentGatewayFormValues>({
            defaultValues,
            mode: 'onChange',
        });

    const enabled = !paymentGateway
        ? Boolean(watch('enabled'))
        : paymentGateway.reference.provider === title.toLowerCase();

    const disableProvider = () => {
        if (!paymentGateway?.id) return;
        removePaymentGateway(paymentGateway.id, {
            onSuccess: () => {
                setValue('enabled', false);
                clearErrors();
                showSuccess('PAYMENT_SETTINGS_CLEARED', {
                    provider: t(title),
                });
            },
            onError: () => {
                showError('PAYMENT_SETTINGS_CLEAR_FAILED', {
                    provider: t(title),
                });
            },
        });
    };

    const handleToggle = (_: ChangeEvent<HTMLInputElement>, value: boolean) => {
        if (isSaving) return;

        if (!value && !!paymentGateway) {
            setIsModalOpen(true);
            return;
        }

        setValue('enabled', value);
        clearErrors();
    };

    const handleSave = async () => {
        const input = buildPayload(getValues(), provider);
        if (!input) return;

        updatePaymentGateway(input, {
            onSuccess: () => {
                showSuccess('PAYMENT_SETTINGS_SAVED', {
                    provider: t(title),
                });
            },
            onError: () => {
                showError('PAYMENT_SETTINGS_SAVE_FAILED', {
                    provider: t(title),
                });
            },
        });
    };

    return (
        <PaymentProviderCard loading={isSaving}>
            <Form>
                <Stack p={2} gap={2}>
                    <PaymentProviderHeader
                        title={t('Enable {{provider}}', {
                            provider: t(title),
                        })}
                        enabled={enabled}
                        allowEnable={
                            paymentGateway
                                ? paymentGateway.reference.provider ===
                                  title.toLowerCase()
                                : true
                        }
                        onToggle={handleToggle}
                        onLabel={t('On')}
                        offLabel={t('Off')}
                    />

                    <Stack gap={1.5}>
                        {enabled && (
                            <Stack gap={1.5}>
                                {fields.map(field => (
                                    <TextField
                                        placeholder={t(field.label)}
                                        label={t(field.label)}
                                        key={field.name}
                                        name={field.name}
                                        type={field.type || 'text'}
                                        showErrors={!!errorFields}
                                        disabled={!!paymentGateway}
                                        fullWidth
                                    />
                                ))}
                            </Stack>
                        )}

                        {enabled && (
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="flex-end"
                                gap={1}
                            >
                                <Button
                                    variant="contained"
                                    onClick={() => void handleSave()}
                                    disabled={isSaving || !!paymentGateway}
                                >
                                    {t('Save')}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
                <PaymentProviderDisableModal
                    isOpen={isModalOpen}
                    setIsOpen={setIsModalOpen}
                    disableProvider={disableProvider}
                />
            </Form>
        </PaymentProviderCard>
    );
};

export default PaymentProviderPage;
